import { useRef } from 'react';

/**
 * @param {React.DragEvent<HTMLElement>} e
 * @returns {{offSetX: number, offSetY: number}}
 */
export const calculateOffSetOnDragStart = (e) => {
  // Calculate the offset between the mouse cursor and the element's top-left corner.
  // This is necessary to ensure the element moves in a way that corresponds to the
  // position where the user clicked (not just from the top-left corner).
  const rect = e.currentTarget.getBoundingClientRect(); // Get the element's bounding rectangle
  const offSetX = e.clientX - rect.left; // Calculate the horizontal distance between cursor and element's left edge
  const offSetY = e.clientY - rect.top; // Calculate the vertical distance between cursor and element's top edge

  return {
    offSetX,
    offSetY,
  };
};

/**
 * @typedef {(e: React.DragEvent<HTMLDivElement>, gridCellSize: number) => {widget: import('./config').Widget, isNew: boolean}} CalculateNewPositionFn
 */

/**
 * @param {number} parentWidth
 * @param {number} parentHeight
 * @returns {{ref: import('react').MutableRefObject<HTMLDivElement>, calculateNewPosition: CalculateNewPositionFn}}
 */
export const useOnDrop = (parentWidth, parentHeight) => {
  /**
   * @type {import('react').MutableRefObject<HTMLDivElement>}
   */
  // @ts-expect-error ref
  const ref = useRef();

  /**
   * @type {CalculateNewPositionFn}
   */
  const calculateNewPosition = (e, gridCellSize = 0) => {
    const droppedElement = JSON.parse(e.dataTransfer.getData('application/json'));
    const { widget, dragProperties } = droppedElement;

    const parent = ref.current.getBoundingClientRect(); // Get the parent's dimensions and position on the screen

    // Calculate the new position for the widget based on where it was dropped
    // Subtract the parent element's position and the widget's offset to align the widget correctly
    let x = e.clientX - parent.x - dragProperties.offSetX;
    let y = e.clientY - parent.y - dragProperties.offSetY;

    // Ensure the widget stays within the bounds of its parent container
    // right side
    if (x + widget.width > parentWidth) {
      x = parentWidth - widget.width; // If the widget extends beyond the parent's right edge, set it to the rightmost position
    }
    // left side
    if (x < 0) {
      x = 0; // If the widget moves beyond the parent's left edge, set it to the leftmost position
    }
    // bottom side
    if (y + widget.height > parentHeight) {
      y = parentHeight - widget.height; // If the widget extends beyond the parent's bottom edge, set it to the bottommost position
    }
    // top side
    if (y < 0) {
      y = 0; // If the widget moves beyond the parent's top edge, set it to the topmost position
    }

    if (gridCellSize !== 0) {
      x = Math.round(x / gridCellSize) * gridCellSize;
      y = Math.round(y / gridCellSize) * gridCellSize;
    } else {
      x = Math.round(x);
      y = Math.round(y);
    }

    return {
      widget: {
        ...widget,
        x,
        y,
      },
      isNew: dragProperties.isNew,
    };
  };

  return {
    ref,
    calculateNewPosition,
  };
};
